import { Cluster, ClusterStatus } from "in_queue/types/clusterType";
import { InQueueSubscription } from "in_queue/types/projectType";
import {
    CostAssumptionType,
    ScenarioMetadata,
    ScenarioStage
} from "in_queue/types/scenarioType";
import { capitalize } from "lodash";
import { clusterStatusFromSubscription } from "./clusterStatus";

// Determine if this cluster and scenario stage allows creation of scenarios.
// Note that the user might have other restrictions that prevent creation, such
// as permissions or subscription status.
export const doesClusterAllowScenarioCreation = (
    clusterStatus: ClusterStatus,
    scenarioStage: ScenarioStage
) => {
    if (scenarioStage === "PREVIEW" && clusterStatus === ClusterStatus.DpMode) {
        return false;
    }
    const allowedStatuses = [
        ClusterStatus.PreviewMode,
        ClusterStatus.DpMode,
        ClusterStatus.OnlyDpMode
    ];
    return allowedStatuses.includes(clusterStatus);
};

// Determine which scenario stages are viewable for a given cluster status.
const getViewableScenarioStages = (
    clusterStatus: ClusterStatus
): Set<ScenarioStage> => {
    const stages: Set<ScenarioStage> = new Set();
    // Cluster statuses that allow viewing the preview scenarios.
    const previewStatuses = [
        ClusterStatus.PreviewButNoScenarios,
        ClusterStatus.PreviewMode,
        ClusterStatus.DpMode,
        ClusterStatus.AfterDp
    ];
    // Cluster statuses that allow viewing the decision point scenarios.
    const dpStatuses = [
        ClusterStatus.DpMode,
        ClusterStatus.OnlyDpMode,
        ClusterStatus.AfterDp
    ];
    if (previewStatuses.includes(clusterStatus)) {
        stages.add("PREVIEW");
    }
    if (dpStatuses.includes(clusterStatus)) {
        stages.add("DECISION_POINT");
    }
    if (!stages.size) {
        console.warn(
            "This cluster is not enabled for preview or decision point."
        );
    }
    return stages;
};

// Determine which scenario stages are viewable for a given subscription.
export const getViewableScenarioStagesForSubscription = (
    cluster: Cluster,
    subscription?: InQueueSubscription
) => {
    const clusterStatus = clusterStatusFromSubscription(cluster, subscription);
    const stages = getViewableScenarioStages(clusterStatus);
    return stages;
};

export const getCostAssumptionText = (
    scenario?: Pick<ScenarioMetadata, "costAssumptions">
): string => {
    if (!scenario) {
        return "-";
    }
    const costAssumption = scenario.costAssumptions.type;
    if (costAssumption === CostAssumptionType.NETWORK_UPGRADES) {
        return "ISO-Provided";
    } else if (costAssumption === CostAssumptionType.SUGAR_UPGRADES) {
        return "MISO Whitepaper Estimates";
    }

    // For estimates, show the specific estimate type.
    return capitalize(scenario.costAssumptions.type);
};

export const shouldShowCostAssumptions = ({
    stage,
    supportedCostAssumptionTypes
}: {
    stage: ScenarioStage;
    supportedCostAssumptionTypes: Record<ScenarioStage, CostAssumptionType[]>;
}) => {
    return supportedCostAssumptionTypes[stage].length > 0;
};
